import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { 
  Building2, 
  Mail, 
  Lock, 
  Phone, 
  User, 
  FileText,
  CreditCard,
  QrCode,
  Loader2,
  Check,
  AlertCircle,
  ArrowLeft,
  Shield
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Checkbox } from "../components/ui/checkbox";
import { API_URL, validarCNPJ } from "../lib/utils";
import { toast } from "../components/ToastProvider";

const PLANOS_INFO = {
  basico: { nome: "Básico", cor: "slate" },
  profissional: { nome: "Profissional", cor: "teal" },
  enterprise: { nome: "Enterprise", cor: "amber" }
};

const CICLOS_INFO = {
  monthly: { label: "Mensal", meses: 1 },
  quarterly: { label: "Trimestral", meses: 3, desconto: "5%" },
  semi_annual: { label: "Semestral", meses: 6, desconto: "10%" },
  annual: { label: "Anual", meses: 12, desconto: "20%" }
};

const Checkout = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const planoParam = searchParams.get("plano") || "basico";
  const cicloParam = searchParams.get("ciclo") || "monthly";
  const codigoIndicacao = searchParams.get("ref") || "";
  
  const [plano, setPlano] = useState(planoParam);
  const [ciclo, setCiclo] = useState(cicloParam);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [precoInfo, setPrecoInfo] = useState(null);
  const [formaPagamento, setFormaPagamento] = useState("PIX");
  const [aceitouTermos, setAceitouTermos] = useState(false);
  const [indicadorInfo, setIndicadorInfo] = useState(null);
  
  const [formData, setFormData] = useState({
    // Dados da Farmácia
    nome_farmacia: "",
    cnpj: "",
    telefone: "",
    // Dados do Usuário
    nome_responsavel: "",
    email: "",
    senha: "",
    confirmar_senha: "",
    whatsapp: ""
  });
  
  const [errors, setErrors] = useState({});
  const cnpjDigits = formData.cnpj.replace(/\D/g, "");
  const cnpjCompleto = cnpjDigits.length === 14 ? cnpjDigits : "";

  useEffect(() => {
    fetchPreco();
    if (codigoIndicacao) {
      validarCodigoIndicacao();
    }
  }, [plano, ciclo, codigoIndicacao, cnpjCompleto]);

  const validarCodigoIndicacao = async () => {
    try {
      const response = await fetch(`${API_URL}/api/indicacao/validar/${codigoIndicacao}`);
      const data = await response.json();
      if (data.valido) {
        setIndicadorInfo(data);
      }
    } catch (error) {
      console.error("Erro ao validar código:", error);
    }
  };

  const fetchPreco = async () => {
    try {
      const response = await fetch(`${API_URL}/api/pagamentos/calcular/${plano}/${ciclo}${cnpjCompleto ? `?cnpj=${cnpjCompleto}` : ""}`);
      const data = await response.json();
      setPrecoInfo(data);
    } catch (error) {
      console.error("Erro ao calcular preço:", error);
      toast.error("Erro ao carregar informações do plano");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Limpar erro do campo quando usuário digita
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const formatCNPJ = (value) => {
    const numbers = value.replace(/\D/g, "");
    return numbers
      .replace(/^(\d{2})(\d)/, "$1.$2")
      .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
      .replace(/\.(\d{3})(\d)/, ".$1/$2")
      .replace(/(\d{4})(\d)/, "$1-$2")
      .slice(0, 18);
  };

  const formatPhone = (value) => {
    const numbers = value.replace(/\D/g, "");
    if (numbers.length <= 10) {
      return numbers
        .replace(/^(\d{2})(\d)/, "($1) $2")
        .replace(/(\d{4})(\d)/, "$1-$2");
    }
    return numbers
      .replace(/^(\d{2})(\d)/, "($1) $2")
      .replace(/(\d{5})(\d)/, "$1-$2")
      .slice(0, 15);
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.nome_farmacia.trim()) {
      newErrors.nome_farmacia = "Nome da farmácia é obrigatório";
    }
    
    const cnpjNumbers = formData.cnpj.replace(/\D/g, "");
    if (cnpjNumbers.length !== 14 || !validarCNPJ(cnpjNumbers)) {
      newErrors.cnpj = "CNPJ inválido. Verifique os dígitos.";
    }
    
    if (!formData.nome_responsavel.trim()) {
      newErrors.nome_responsavel = "Nome do responsável é obrigatório";
    }
    
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Email inválido";
    }
    
    if (formData.senha.length < 6) {
      newErrors.senha = "Senha deve ter pelo menos 6 caracteres";
    }
    
    if (formData.senha !== formData.confirmar_senha) {
      newErrors.confirmar_senha = "Senhas não conferem";
    }
    
    if (!aceitouTermos) {
      newErrors.termos = "Você deve aceitar os termos de uso";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error("Por favor, corrija os erros no formulário");
      return;
    }
    
    setProcessing(true);
    
    try {
      const response = await fetch(`${API_URL}/api/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          // Dados da farmácia
          nome_farmacia: formData.nome_farmacia,
          cnpj: formData.cnpj.replace(/\D/g, ""),
          telefone: formData.telefone.replace(/\D/g, ""),
          // Dados do usuário
          nome_responsavel: formData.nome_responsavel,
          email: formData.email,
          senha: formData.senha,
          whatsapp: formData.whatsapp.replace(/\D/g, ""),
          // Dados da assinatura
          plano,
          ciclo,
          forma_pagamento: formaPagamento,
          // Código de indicação (se houver)
          codigo_indicacao: codigoIndicacao || null
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || "Erro ao processar cadastro");
      }
      
      // Disparar evento sign_up no GTM (cadastro via checkout)
      if (window.dataLayer) {
        window.dataLayer.push({
          'event': 'sign_up',
          'method': 'checkout_form'
        });
      }
      
      toast.success("Cadastro realizado com sucesso!");
      
      // Redirecionar para página de sucesso ou login
      navigate("/checkout/sucesso", { 
        state: { 
          email: formData.email,
          plano: precoInfo?.plano_nome,
          ciclo: ciclo,
          valor: precoInfo?.preco_total,
          paymentInfo: data.payment_info
        }
      });
      
    } catch (error) {
      console.error("Erro no checkout:", error);
      toast.error(error.message || "Erro ao processar cadastro");
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-teal-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  const planoInfo = PLANOS_INFO[plano] || PLANOS_INFO.basico;
  const cicloInfo = CICLOS_INFO[ciclo] || CICLOS_INFO.monthly;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-teal-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center text-teal-600 hover:text-teal-700 mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Link>
          <h1 className="text-3xl font-bold text-slate-800">Finalizar Assinatura</h1>
          <p className="text-slate-600 mt-2">Complete seu cadastro para começar a usar o sistema</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Formulário de Cadastro */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle>Dados do Cadastro</CardTitle>
                <CardDescription>Preencha os dados da sua farmácia</CardDescription>
              </CardHeader>
              <CardContent>
                {/* Badge de Indicação */}
                {indicadorInfo && (
                  <div className="mb-6 p-4 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg">
                    <div className="flex items-center gap-2">
                      <span className="text-purple-600 text-lg">🎉</span>
                      <div>
                        <p className="font-medium text-purple-900 dark:text-purple-100">
                          Você foi indicado por <strong>{indicadorInfo.indicador}</strong>
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Seleção de Plano */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-2">
                      <CreditCard className="w-4 h-4" />
                      Escolha seu Plano
                    </h3>
                    
                    <div className="grid grid-cols-3 gap-3">
                      {Object.entries({
                        basico: { nome: "Básico", preco: "R$ 109", usuarios: "2 usuários" },
                        profissional: { nome: "Profissional", preco: "R$ 149", usuarios: "6 usuários" },
                        enterprise: { nome: "Enterprise", preco: "R$ 179", usuarios: "11 usuários" }
                      }).map(([key, info]) => (
                        <button
                          key={key}
                          type="button"
                          onClick={() => setPlano(key)}
                          className={`p-4 rounded-lg border-2 transition-all text-left ${
                            plano === key 
                              ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20' 
                              : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
                          }`}
                        >
                          <p className={`font-semibold ${plano === key ? 'text-teal-700 dark:text-teal-400' : 'text-slate-700 dark:text-slate-300'}`}>
                            {info.nome}
                          </p>
                          <p className="text-sm text-slate-500 dark:text-slate-400">{info.preco}/mês</p>
                          <p className="text-xs text-slate-400 dark:text-slate-500">{info.usuarios}</p>
                        </button>
                      ))}
                    </div>
                    
                    {/* Seleção de Ciclo */}
                    <div className="mt-4">
                      <Label className="text-sm text-slate-600 dark:text-slate-400 mb-2 block">Ciclo de pagamento</Label>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {Object.entries({
                          monthly: { label: "Mensal", desconto: null },
                          quarterly: { label: "Trimestral", desconto: "5% off" },
                          semi_annual: { label: "Semestral", desconto: "10% off" },
                          annual: { label: "Anual", desconto: "20% off" }
                        }).map(([key, info]) => (
                          <button
                            key={key}
                            type="button"
                            onClick={() => setCiclo(key)}
                            className={`p-2 rounded-lg border transition-all text-center ${
                              ciclo === key 
                                ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20' 
                                : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
                            }`}
                          >
                            <p className={`text-sm font-medium ${ciclo === key ? 'text-teal-700 dark:text-teal-400' : 'text-slate-600 dark:text-slate-400'}`}>
                              {info.label}
                            </p>
                            {info.desconto && (
                              <p className="text-xs text-green-600 dark:text-green-400">{info.desconto}</p>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Dados da Farmácia */}
                  <div className="space-y-4 pt-4 border-t">
                    <h3 className="font-semibold text-slate-700 flex items-center gap-2">
                      <Building2 className="w-4 h-4" />
                      Dados da Farmácia
                    </h3>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="nome_farmacia">Nome da Farmácia *</Label>
                        <Input
                          id="nome_farmacia"
                          value={formData.nome_farmacia}
                          onChange={(e) => handleChange("nome_farmacia", e.target.value)}
                          placeholder="Ex: Farmácia São João"
                          className={errors.nome_farmacia ? "border-red-500" : ""}
                        />
                        {errors.nome_farmacia && (
                          <p className="text-xs text-red-500">{errors.nome_farmacia}</p>
                        )}
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="cnpj">CNPJ *</Label>
                        <Input
                          id="cnpj"
                          value={formData.cnpj}
                          onChange={(e) => handleChange("cnpj", formatCNPJ(e.target.value))}
                          placeholder="00.000.000/0000-00"
                          className={errors.cnpj ? "border-red-500" : ""}
                        />
                        {errors.cnpj && (
                          <p className="text-xs text-red-500">{errors.cnpj}</p>
                        )}
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="telefone">Telefone da Farmácia</Label>
                      <Input
                        id="telefone"
                        value={formData.telefone}
                        onChange={(e) => handleChange("telefone", formatPhone(e.target.value))}
                        placeholder="(00) 00000-0000"
                      />
                    </div>
                  </div>

                  {/* Dados do Responsável */}
                  <div className="space-y-4 pt-4 border-t">
                    <h3 className="font-semibold text-slate-700 flex items-center gap-2">
                      <User className="w-4 h-4" />
                      Dados do Responsável (Admin)
                    </h3>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="nome_responsavel">Nome Completo *</Label>
                        <Input
                          id="nome_responsavel"
                          value={formData.nome_responsavel}
                          onChange={(e) => handleChange("nome_responsavel", e.target.value)}
                          placeholder="Seu nome completo"
                          className={errors.nome_responsavel ? "border-red-500" : ""}
                        />
                        {errors.nome_responsavel && (
                          <p className="text-xs text-red-500">{errors.nome_responsavel}</p>
                        )}
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="whatsapp">WhatsApp</Label>
                        <Input
                          id="whatsapp"
                          value={formData.whatsapp}
                          onChange={(e) => handleChange("whatsapp", formatPhone(e.target.value))}
                          placeholder="(00) 00000-0000"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleChange("email", e.target.value)}
                          placeholder="seu@email.com"
                          className={`pl-10 ${errors.email ? "border-red-500" : ""}`}
                        />
                      </div>
                      {errors.email && (
                        <p className="text-xs text-red-500">{errors.email}</p>
                      )}
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="senha">Senha *</Label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <Input
                            id="senha"
                            type="password"
                            value={formData.senha}
                            onChange={(e) => handleChange("senha", e.target.value)}
                            placeholder="Mínimo 6 caracteres"
                            className={`pl-10 ${errors.senha ? "border-red-500" : ""}`}
                          />
                        </div>
                        {errors.senha && (
                          <p className="text-xs text-red-500">{errors.senha}</p>
                        )}
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="confirmar_senha">Confirmar Senha *</Label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <Input
                            id="confirmar_senha"
                            type="password"
                            value={formData.confirmar_senha}
                            onChange={(e) => handleChange("confirmar_senha", e.target.value)}
                            placeholder="Repita a senha"
                            className={`pl-10 ${errors.confirmar_senha ? "border-red-500" : ""}`}
                          />
                        </div>
                        {errors.confirmar_senha && (
                          <p className="text-xs text-red-500">{errors.confirmar_senha}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Forma de Pagamento */}
                  <div className="space-y-4 pt-4 border-t">
                    <h3 className="font-semibold text-slate-700 flex items-center gap-2">
                      <CreditCard className="w-4 h-4" />
                      Forma de Pagamento
                    </h3>
                    
                    <RadioGroup 
                      value={formaPagamento} 
                      onValueChange={setFormaPagamento}
                      className="grid grid-cols-3 gap-3"
                    >
                      <div>
                        <RadioGroupItem value="PIX" id="checkout-pix" className="peer sr-only" />
                        <Label
                          htmlFor="checkout-pix"
                          className="flex flex-col items-center justify-center rounded-lg border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-teal-600 cursor-pointer transition-all"
                        >
                          <QrCode className="w-6 h-6 mb-2" />
                          <span className="font-medium">PIX</span>
                          <span className="text-xs text-slate-500">Aprovação imediata</span>
                        </Label>
                      </div>
                      <div>
                        <RadioGroupItem value="BOLETO" id="checkout-boleto" className="peer sr-only" />
                        <Label
                          htmlFor="checkout-boleto"
                          className="flex flex-col items-center justify-center rounded-lg border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-teal-600 cursor-pointer transition-all"
                        >
                          <FileText className="w-6 h-6 mb-2" />
                          <span className="font-medium">Boleto</span>
                          <span className="text-xs text-slate-500">Até 3 dias úteis</span>
                        </Label>
                      </div>
                      <div>
                        <RadioGroupItem value="CREDIT_CARD" id="checkout-card" className="peer sr-only" />
                        <Label
                          htmlFor="checkout-card"
                          className="flex flex-col items-center justify-center rounded-lg border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-teal-600 cursor-pointer transition-all"
                        >
                          <CreditCard className="w-6 h-6 mb-2" />
                          <span className="font-medium">Cartão</span>
                          <span className="text-xs text-slate-500">Aprovação imediata</span>
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>

                  {/* Termos */}
                  <div className="flex items-start space-x-2 pt-4">
                    <Checkbox
                      id="termos"
                      checked={aceitouTermos}
                      onCheckedChange={setAceitouTermos}
                      className={errors.termos ? "border-red-500" : ""}
                    />
                    <div className="grid gap-1.5 leading-none">
                      <Label
                        htmlFor="termos"
                        className="text-sm font-normal cursor-pointer"
                      >
                        Li e aceito os{" "}
                        <Link to="/termos" className="text-teal-600 hover:underline" target="_blank">
                          Termos de Uso
                        </Link>{" "}
                        e a{" "}
                        <Link to="/termos" className="text-teal-600 hover:underline" target="_blank">
                          Política de Privacidade
                        </Link>
                      </Label>
                      {errors.termos && (
                        <p className="text-xs text-red-500">{errors.termos}</p>
                      )}
                    </div>
                  </div>

                  {/* Botão de Submit */}
                  <Button
                    type="submit"
                    className="w-full bg-teal-600 hover:bg-teal-700 h-12 text-lg"
                    disabled={processing}
                  >
                    {processing ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Processando...
                      </>
                    ) : (
                      <>
                        <Shield className="w-5 h-5 mr-2" />
                        Finalizar Assinatura
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Resumo do Pedido */}
          <div className="lg:col-span-1">
            <Card className="sticky top-8">
              <CardHeader className={`bg-${planoInfo.cor}-50 rounded-t-lg`}>
                <CardTitle className="text-lg">Resumo do Pedido</CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <div className="flex justify-between items-center pb-4 border-b">
                    <div>
                      <p className="font-semibold text-lg">{precoInfo?.plano_nome}</p>
                      <p className="text-sm text-slate-500">{cicloInfo.label}</p>
                    </div>
                    {cicloInfo.desconto && (
                      <span className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded">
                        -{cicloInfo.desconto}
                      </span>
                    )}
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Valor mensal</span>
                      <span>R$ {precoInfo?.preco_mensal?.toFixed(2)}</span>
                    </div>
                    {cicloInfo.meses > 1 && (
                      <div className="flex justify-between">
                        <span className="text-slate-600">Período</span>
                        <span>{cicloInfo.meses} meses</span>
                      </div>
                    )}
                    {precoInfo?.desconto_percentual > 0 && (
                      <div className="flex justify-between text-green-600">
                        <span>Desconto</span>
                        <span>-{precoInfo.desconto_percentual}%</span>
                      </div>
                    )}
                    {precoInfo?.desconto_rede_percentual > 0 && (
                      <div className="flex justify-between text-teal-700" data-testid="checkout-desconto-rede">
                        <span>Desconto de rede ({precoInfo.rede?.posicao}º CNPJ{precoInfo.rede?.matriz_nome ? ` · ${precoInfo.rede.matriz_nome}` : ""})</span>
                        <span>-{precoInfo.desconto_rede_percentual}%</span>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-between items-center pt-4 border-t">
                    <span className="font-semibold">Total</span>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-teal-600">
                        R$ {precoInfo?.preco_total?.toFixed(2)}
                      </p>
                      {cicloInfo.meses > 1 && (
                        <p className="text-xs text-slate-500">
                          ou R$ {precoInfo?.preco_mensal?.toFixed(2)}/mês
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Benefícios */}
                  <div className="pt-4 border-t space-y-2">
                    <p className="text-sm font-medium text-slate-700">Incluso no plano:</p>
                    <ul className="space-y-1.5 text-sm text-slate-600">
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-500" />
                        Documentos ilimitados
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-500" />
                        Verificações diárias
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-500" />
                        Alertas por email
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-500" />
                        Suporte por ticket
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-500" />
                        Relatórios avançados
                      </li>
                    </ul>
                  </div>

                  <Alert className="bg-slate-50">
                    <Shield className="h-4 w-4" />
                    <AlertDescription className="text-xs">
                      Pagamento seguro processado pelo Asaas. Seus dados estão protegidos.
                    </AlertDescription>
                  </Alert>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
